/* eslint-disable no-bitwise */
/**
 * Pure TypeScript implementations of the logarithm, exponential, and power functions used by Excess.
 *
 * Native `Math.log`, `Math.log10`, `Math.exp`, and `Math.pow` are "implementation-approximated" by
 * the ECMAScript spec, so browser engines and Node versions return results that differ in the last
 * bits for some inputs. Evaluation snapshots are produced both in the user's browser and by the
 * Notebook Evaluation Service on Node, so formula results must not depend on the JavaScript engine.
 * These functions use only IEEE 754 basic arithmetic, which is exactly specified, so they return
 * bit-identical results in every engine.
 *
 * Provenance
 * ----------
 * `log`, `log10`, `exp`, and `pow` are line-by-line TypeScript translations of V8's fdlibm-derived
 * `src/base/ieee754.cc` at commit 32d567d072a1a62e894619d8e13485aed3889382, which is what V8 used for
 * these `Math` functions before switching to LLVM's libm:
 * https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/src/base/ieee754.cc
 *
 * Functions translated:
 * - `exp`: lines 1457-1549
 *   https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/src/base/ieee754.cc#L1457-L1549
 * - `log`: lines 1650-1731
 *   https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/src/base/ieee754.cc#L1650-L1731
 * - `log10`: lines 2097-2135
 *   https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/src/base/ieee754.cc#L2097-L2135
 * - `pow`: `legacy::pow`, lines 2586-2847. At this commit V8 kept the fdlibm implementation in a
 *   `legacy` namespace, used for `Math.pow` under `--no-use-std-math-pow`.
 *   https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/src/base/ieee754.cc#L2586-L2847
 *
 * Numeric constants, their hex-word comments, and branch structure match the source. V8's
 * `GET_HIGH_WORD`/`GET_LOW_WORD`/`SET_HIGH_WORD`/`SET_LOW_WORD`/`INSERT_WORDS` macros are replaced by
 * typed-array helpers, snake_case identifiers are camelCased, and decimal literals with more digits
 * than a double holds are written as the shortest literal for the same double.
 *
 * V8's `pow` calls the C library's `scalbn` from `<cmath>`; the `scalbn` here is translated from musl
 * libc (see the notice on `scalbn` below).
 *
 * Results are bit-identical to `Math.*` in V8 versions that used fdlibm for these functions,
 * including Node 22 and Node 24 (for `pow`, Node 24 only with `--no-use-std-math-pow`).
 *
 * ====================================================
 * Copyright (C) 1993 by Sun Microsystems, Inc. All rights reserved.
 *
 * Developed at SunSoft, a Sun Microsystems, Inc. business.
 * Permission to use, copy, modify, and distribute this
 * software is freely granted, provided that this notice
 * is preserved.
 * ====================================================
 *
 * The original source code covered by the above license has been
 * modified significantly by Google Inc.
 * Copyright 2016 the V8 project authors. All rights reserved.
 * Use of this source code is governed by a BSD-style license that can be
 * found at https://github.com/v8/v8/blob/32d567d072a1a62e894619d8e13485aed3889382/LICENSE.
 */

const float64 = new Float64Array(1);
const int32 = new Int32Array(float64.buffer);
const IS_LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;
const HIGH = IS_LITTLE_ENDIAN ? 1 : 0;
const LOW = IS_LITTLE_ENDIAN ? 0 : 1;

/** The sign, exponent, and upper 20 mantissa bits of a double, as a signed 32-bit integer. */
function getHighWord(d: number): number {
  float64[0] = d;
  return int32[HIGH];
}

/** The lower 32 mantissa bits of a double, as an unsigned 32-bit integer. */
function getLowWord(d: number): number {
  float64[0] = d;
  return int32[LOW] >>> 0;
}

function setHighWord(d: number, high: number): number {
  float64[0] = d;
  int32[HIGH] = high;
  return float64[0];
}

function setLowWord(d: number, low: number): number {
  float64[0] = d;
  int32[LOW] = low;
  return float64[0];
}

function insertWords(high: number, low: number): number {
  int32[HIGH] = high;
  int32[LOW] = low;
  return float64[0];
}

const TWO_1023 = insertWords(0x7fe00000, 0);
const TWO_M969 = insertWords(0x03600000, 0);

/**
 * Returns x * 2^n rounded once, matching C's `scalbn`. Intermediate scaling keeps the value normal
 * so that only the final multiplication can round.
 *
 * Translated from musl libc's `src/math/scalbn.c` (the whole `scalbn` function, lines 4-33) at
 * commit 8c44a060243f04283ca68dad199aab90336141db:
 * https://git.musl-libc.org/cgit/musl/tree/src/math/scalbn.c?id=8c44a060243f04283ca68dad199aab90336141db#n4
 * `0x1p1023` is written as `TWO_1023` and `0x1p-1022 * 0x1p53` as `TWO_M969`, and the 64-bit union
 * write of the scale factor is replaced by `insertWords`.
 *
 * musl is licensed under the following MIT license
 * (https://git.musl-libc.org/cgit/musl/tree/COPYRIGHT):
 *
 * ----------------------------------------------------------------------
 * Copyright © 2005-2020 Rich Felker, et al.
 *
 * Permission is hereby granted, free of charge, to any person obtaining
 * a copy of this software and associated documentation files (the
 * "Software"), to deal in the Software without restriction, including
 * without limitation the rights to use, copy, modify, merge, publish,
 * distribute, sublicense, and/or sell copies of the Software, and to
 * permit persons to whom the Software is furnished to do so, subject to
 * the following conditions:
 *
 * The above copyright notice and this permission notice shall be
 * included in all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 * EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 * MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 * IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 * CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 * TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 * SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 * ----------------------------------------------------------------------
 */
function scalbn(x: number, n: number): number {
  let y = x;
  let e = n;
  if (e > 1023) {
    y *= TWO_1023;
    e -= 1023;
    if (e > 1023) {
      y *= TWO_1023;
      e -= 1023;
      if (e > 1023) {
        e = 1023;
      }
    }
  } else if (e < -1022) {
    y *= TWO_M969;
    e += 969;
    if (e < -1022) {
      y *= TWO_M969;
      e += 969;
      if (e < -1022) {
        e = -1022;
      }
    }
  }
  return y * insertWords((0x3ff + e) << 20, 0);
}

const LN2_HI = 6.9314718036912381649e-1; // 0x3FE62E42, 0xFEE00000
const LN2_LO = 1.90821492927058770002e-10; // 0x3DEA39EF, 0x35793C76
const TWO54 = 1.8014398509481984e16; // 0x43500000, 0x00000000

/** Natural logarithm, matching fdlibm `log`. */
export function log(input: number): number {
  const Lg1 = 6.66666666666673513e-1; // 3FE55555 55555593
  const Lg2 = 3.999999999940941908e-1; // 3FD99999 9997FA04
  const Lg3 = 2.857142874366239149e-1; // 3FD24924 94229359
  const Lg4 = 2.222219843214978396e-1; // 3FCC71C5 1D8E78AF
  const Lg5 = 1.818357216161805012e-1; // 3FC74664 96CB03DE
  const Lg6 = 1.531383769920937332e-1; // 3FC39A09 D078C69F
  const Lg7 = 1.479819860511658591e-1; // 3FC2F112 DF3E5244

  let x = input;
  let hx = getHighWord(x);
  const lx = getLowWord(x);

  let k = 0;
  if (hx < 0x00100000) {
    // x < 2**-1022
    if (((hx & 0x7fffffff) | lx) === 0) {
      return -Infinity; // log(+-0) = -inf
    }
    if (hx < 0) {
      return NaN; // log(-#) = NaN
    }
    k -= 54;
    x *= TWO54; // subnormal number, scale up x
    hx = getHighWord(x);
  }
  if (hx >= 0x7ff00000) {
    return x + x;
  }
  k += (hx >> 20) - 1023;
  hx &= 0x000fffff;
  let i = (hx + 0x95f64) & 0x100000;
  x = setHighWord(x, hx | (i ^ 0x3ff00000)); // normalize x or x/2
  k += i >> 20;
  const f = x - 1.0;
  let dk: number;
  let R: number;
  if ((0x000fffff & (2 + hx)) < 3) {
    // -2**-20 <= f < 2**-20
    if (f === 0) {
      if (k === 0) {
        return 0;
      }
      dk = k;
      return dk * LN2_HI + dk * LN2_LO;
    }
    R = f * f * (0.5 - 0.3333333333333333 * f);
    if (k === 0) {
      return f - R;
    }
    dk = k;
    return dk * LN2_HI - (R - dk * LN2_LO - f);
  }
  const s = f / (2.0 + f);
  dk = k;
  const z = s * s;
  i = hx - 0x6147a;
  const w = z * z;
  const j = 0x6b851 - hx;
  const t1 = w * (Lg2 + w * (Lg4 + w * Lg6));
  const t2 = z * (Lg1 + w * (Lg3 + w * (Lg5 + w * Lg7)));
  i |= j;
  R = t2 + t1;
  if (i > 0) {
    const hfsq = 0.5 * f * f;
    if (k === 0) {
      return f - (hfsq - s * (hfsq + R));
    }
    return dk * LN2_HI - (hfsq - (s * (hfsq + R) + dk * LN2_LO) - f);
  }
  if (k === 0) {
    return f - s * (f - R);
  }
  return dk * LN2_HI - (s * (f - R) - dk * LN2_LO - f);
}

/** Base 10 logarithm, matching fdlibm `log10`. log10(10**n) = n for n = 0..22. */
export function log10(input: number): number {
  const ivln10 = 4.34294481903251816668e-1;
  const log10Of2Hi = 3.01029995663611771306e-1; // 0x3FD34413, 0x509F6000
  const log10Of2Lo = 3.69423907715893078616e-13; // 0x3D59FEF3, 0x11F12B36

  let x = input;
  let hx = getHighWord(x);
  let lx = getLowWord(x);

  let k = 0;
  if (hx < 0x00100000) {
    // x < 2**-1022
    if (((hx & 0x7fffffff) | lx) === 0) {
      return -Infinity; // log(+-0) = -inf
    }
    if (hx < 0) {
      return NaN; // log(-#) = NaN
    }
    k -= 54;
    x *= TWO54; // subnormal number, scale up x
    hx = getHighWord(x);
    lx = getLowWord(x);
  }
  if (hx >= 0x7ff00000) {
    return x + x;
  }
  if (hx === 0x3ff00000 && lx === 0) {
    return 0; // log(1) = +0
  }
  k += (hx >> 20) - 1023;

  const i = (k & 0x80000000) >>> 31;
  hx = (hx & 0x000fffff) | ((0x3ff - i) << 20);
  const y = k + i;
  x = setHighWord(x, hx);
  x = setLowWord(x, lx);

  const z = y * log10Of2Lo + ivln10 * log(x);
  return z + y * log10Of2Hi;
}

/** e raised to the power x, matching fdlibm `exp`. */
export function exp(input: number): number {
  const halF = [0.5, -0.5];
  const oThreshold = 7.09782712893383973096e2; // 0x40862E42, 0xFEFA39EF
  const uThreshold = -7.4513321910194110842e2; // 0xC0874910, 0xD52D3051
  const ln2HI = [6.9314718036912381649e-1, -6.9314718036912381649e-1]; // 0x3FE62E42, 0xFEE00000
  const ln2LO = [1.90821492927058770002e-10, -1.90821492927058770002e-10]; // 0x3DEA39EF, 0x35793C76
  const invln2 = 1.442695040888963387; // 0x3FF71547, 0x652B82FE
  const P1 = 1.66666666666666019037e-1; // 0x3FC55555, 0x5555553E
  const P2 = -2.77777777770155933842e-3; // 0xBF66C16C, 0x16BEBD93
  const P3 = 6.61375632143793436117e-5; // 0x3F11566A, 0xAF25DE2C
  const P4 = -1.6533902205465251539e-6; // 0xBEBBBD41, 0xC5D26BF1
  const P5 = 4.13813679705723846039e-8; // 0x3E663769, 0x72BEA4D0
  const E = 2.718281828459045; // 0x4005BF0A, 0x8B145769
  const huge = 1.0e300;
  const twom1000 = 9.3326361850321887899e-302; // 2**-1000
  const two1023 = 8.988465674311579539e307; // 2**1023

  let x = input;
  let hi = 0;
  let lo = 0;
  let k = 0;

  const signedHx = getHighWord(x);
  const xsb = (signedHx >>> 31) & 1; // sign bit of x
  const hx = signedHx & 0x7fffffff; // high word of |x|

  // filter out non-finite argument
  if (hx >= 0x40862e42) {
    // |x| >= 709.78...
    if (hx >= 0x7ff00000) {
      const lx = getLowWord(x);
      if (((hx & 0xfffff) | lx) !== 0) {
        return x + x; // NaN
      }
      return xsb === 0 ? x : 0; // exp(+-inf) = {inf, 0}
    }
    if (x > oThreshold) {
      return huge * huge; // overflow
    }
    if (x < uThreshold) {
      return twom1000 * twom1000; // underflow
    }
  }

  // argument reduction
  if (hx > 0x3fd62e42) {
    // |x| > 0.5 ln2
    if (hx < 0x3ff0a2b2) {
      // and |x| < 1.5 ln2
      // fdlibm special-cases exp(1) because the computation below gets its last bit wrong.
      if (x === 1.0) {
        return E;
      }
      hi = x - ln2HI[xsb];
      lo = ln2LO[xsb];
      k = 1 - xsb - xsb;
    } else {
      k = (invln2 * x + halF[xsb]) | 0;
      const kd = k;
      hi = x - kd * ln2HI[0]; // kd * ln2HI is exact here
      lo = kd * ln2LO[0];
    }
    x = hi - lo;
  } else if (hx < 0x3e300000) {
    // |x| < 2**-28
    if (huge + x > 1) {
      return 1 + x;
    }
  } else {
    k = 0;
  }

  // x is now in primary range
  const t = x * x;
  const twopk =
    k >= -1021 ? insertWords(0x3ff00000 + (k << 20), 0) : insertWords(0x3ff00000 + ((k + 1000) << 20), 0);
  const c = x - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
  if (k === 0) {
    return 1 - ((x * c) / (c - 2.0) - x);
  }
  const y = 1 - (lo - (x * c) / (2.0 - c) - hi);
  if (k >= -1021) {
    if (k === 1024) {
      return y * 2.0 * two1023;
    }
    return y * twopk;
  }
  return y * twopk * twom1000;
}

/**
 * x raised to the power y, matching V8's fdlibm-based `Math.pow` and the `**` operator, including the
 * ECMAScript result of NaN for (+-1) ** (+-Infinity).
 */
export function pow(x: number, y: number): number {
  const bp = [1.0, 1.5];
  const dpH = [0.0, 5.84962487220764160156e-1]; // 0x3FE2B803, 0x40000000
  const dpL = [0.0, 1.35003920212974897128e-8]; // 0x3E4CFDEB, 0x43CFD006
  const two53 = 9007199254740992.0; // 0x43400000, 0x00000000
  const huge = 1.0e300;
  const tiny = 1.0e-300;
  // poly coefs for (3/2)*(log(x)-2s-2/3*s**3
  const L1 = 5.99999999999994648725e-1; // 0x3FE33333, 0x33333303
  const L2 = 4.28571428578550184252e-1; // 0x3FDB6DB6, 0xDB6FABFF
  const L3 = 3.33333329818377432918e-1; // 0x3FD55555, 0x518F264D
  const L4 = 2.72728123808534006489e-1; // 0x3FD17460, 0xA91D4101
  const L5 = 2.30660745775561754067e-1; // 0x3FCD864A, 0x93C9DB65
  const L6 = 2.06975017800338417784e-1; // 0x3FCA7E28, 0x4A454EEF
  const P1 = 1.66666666666666019037e-1; // 0x3FC55555, 0x5555553E
  const P2 = -2.77777777770155933842e-3; // 0xBF66C16C, 0x16BEBD93
  const P3 = 6.61375632143793436117e-5; // 0x3F11566A, 0xAF25DE2C
  const P4 = -1.6533902205465251539e-6; // 0xBEBBBD41, 0xC5D26BF1
  const P5 = 4.13813679705723846039e-8; // 0x3E663769, 0x72BEA4D0
  const lg2 = 6.93147180559945286227e-1; // 0x3FE62E42, 0xFEFA39EF
  const lg2H = 6.93147182464599609375e-1; // 0x3FE62E43, 0x00000000
  const lg2L = -1.90465429995776804525e-9; // 0xBE205C61, 0x0CA86C39
  const ovt = 8.008566259537294e-17; // -(1024-log2(ovfl+.5ulp))
  const cp = 9.61796693925975554329e-1; // 0x3FEEC709, 0xDC3A03FD = 2/(3ln2)
  const cpH = 9.61796700954437255859e-1; // 0x3FEEC709, 0xE0000000 = (float)cp
  const cpL = -7.02846165095275826516e-9; // 0xBE3E2FE0, 0x145B01F5 = tail cpH
  const ivln2 = 1.442695040888963387; // 0x3FF71547, 0x652B82FE = 1/ln2
  const ivln2H = 1.44269502162933349609; // 0x3FF71547, 0x60000000 = 24b 1/ln2
  const ivln2L = 1.92596299112661746887e-8; // 0x3E54AE0B, 0xF85DDF44 = 1/ln2 tail

  let z: number;
  let ax: number;
  let pH: number;
  let pL: number;
  let t1: number;
  let t2: number;
  let r: number;
  let t: number;
  let u: number;
  let v: number;
  let w: number;
  let i: number;
  let j: number;
  let k: number;
  let n: number;

  const hx = getHighWord(x);
  const lx = getLowWord(x);
  const hy = getHighWord(y);
  const ly = getLowWord(y);
  let ix = hx & 0x7fffffff;
  const iy = hy & 0x7fffffff;

  // y == zero: x**0 = 1
  if ((iy | ly) === 0) {
    return 1;
  }

  // +-NaN return x+y
  if (
    ix > 0x7ff00000 ||
    (ix === 0x7ff00000 && lx !== 0) ||
    iy > 0x7ff00000 ||
    (iy === 0x7ff00000 && ly !== 0)
  ) {
    return x + y;
  }

  // determine if y is an odd int when x < 0
  // yisint = 0 ... y is not an integer
  // yisint = 1 ... y is an odd int
  // yisint = 2 ... y is an even int
  let yisint = 0;
  if (hx < 0) {
    if (iy >= 0x43400000) {
      yisint = 2; // even integer y
    } else if (iy >= 0x3ff00000) {
      k = (iy >> 20) - 0x3ff; // exponent
      if (k > 20) {
        j = (ly >>> (52 - k)) | 0;
        if (j << (52 - k) === (ly | 0)) {
          yisint = 2 - (j & 1);
        }
      } else if (ly === 0) {
        j = iy >> (20 - k);
        if (j << (20 - k) === iy) {
          yisint = 2 - (j & 1);
        }
      }
    }
  }

  // special value of y
  if (ly === 0) {
    if (iy === 0x7ff00000) {
      // y is +-inf
      if (((ix - 0x3ff00000) | lx) === 0) {
        return y - y; // inf**+-1 is NaN
      }
      if (ix >= 0x3ff00000) {
        return hy >= 0 ? y : 0; // (|x|>1)**+-inf = inf,0
      }
      return hy < 0 ? -y : 0; // (|x|<1)**-,+inf = inf,0
    }
    if (iy === 0x3ff00000) {
      // y is +-1
      return hy < 0 ? 1 / x : x;
    }
    if (hy === 0x40000000) {
      return x * x; // y is 2
    }
    if (hy === 0x3fe00000 && hx >= 0) {
      // y is 0.5 and x >= +0
      return Math.sqrt(x);
    }
  }

  ax = Math.abs(x);
  // special value of x
  if (lx === 0) {
    if (ix === 0x7ff00000 || ix === 0 || ix === 0x3ff00000) {
      z = ax; // x is +-0,+-inf,+-1
      if (hy < 0) {
        z = 1 / z; // z = (1/|x|)
      }
      if (hx < 0) {
        if (((ix - 0x3ff00000) | yisint) === 0) {
          z = NaN; // (-1)**non-int is NaN
        } else if (yisint === 1) {
          z = -z; // (x<0)**odd = -(|x|**odd)
        }
      }
      return z;
    }
  }

  n = (hx >> 31) + 1;

  // (x<0)**(non-int) is NaN
  if ((n | yisint) === 0) {
    return NaN;
  }

  let s = 1; // s (sign of result -ve**odd) = -1 else = 1
  if ((n | (yisint - 1)) === 0) {
    s = -1; // (-ve)**(odd int)
  }

  if (iy > 0x41e00000) {
    // |y| is huge: |y| > 2**31
    if (iy > 0x43f00000) {
      // |y| > 2**64, must o/uflow
      if (ix <= 0x3fefffff) {
        return hy < 0 ? huge * huge : tiny * tiny;
      }
      if (ix >= 0x3ff00000) {
        return hy > 0 ? huge * huge : tiny * tiny;
      }
    }
    // over/underflow if x is not close to one
    if (ix < 0x3fefffff) {
      return hy < 0 ? s * huge * huge : s * tiny * tiny;
    }
    if (ix > 0x3ff00000) {
      return hy > 0 ? s * huge * huge : s * tiny * tiny;
    }
    // now |1-x| is tiny <= 2**-20, suffice to compute log(x) by x-x^2/2+x^3/3-x^4/4
    t = ax - 1; // t has 20 trailing zeros
    w = t * t * (0.5 - t * (0.3333333333333333 - t * 0.25));
    u = ivln2H * t; // ivln2H has 21 sig. bits
    v = t * ivln2L - w * ivln2;
    t1 = setLowWord(u + v, 0);
    t2 = v - (t1 - u);
  } else {
    n = 0;
    // take care subnormal number
    if (ix < 0x00100000) {
      ax *= two53;
      n -= 53;
      ix = getHighWord(ax);
    }
    n += (ix >> 20) - 0x3ff;
    j = ix & 0x000fffff;
    // determine interval
    ix = j | 0x3ff00000; // normalize ix
    if (j <= 0x3988e) {
      k = 0; // |x|<sqrt(3/2)
    } else if (j < 0xbb67a) {
      k = 1; // |x|<sqrt(3)
    } else {
      k = 0;
      n += 1;
      ix -= 0x00100000;
    }
    ax = setHighWord(ax, ix);

    // compute ss = sH+sL = (x-1)/(x+1) or (x-1.5)/(x+1.5)
    u = ax - bp[k]; // bp[0]=1.0, bp[1]=1.5
    v = 1 / (ax + bp[k]);
    const ss = u * v;
    const sH = setLowWord(ss, 0);
    // tH=ax+bp[k] High
    let tH = insertWords(((ix >> 1) | 0x20000000) + 0x00080000 + (k << 18), 0);
    let tL = ax - (tH - bp[k]);
    const sL = v * (u - sH * tH - sH * tL);
    // compute log(ax)
    let s2 = ss * ss;
    r = s2 * s2 * (L1 + s2 * (L2 + s2 * (L3 + s2 * (L4 + s2 * (L5 + s2 * L6)))));
    r += sL * (sH + ss);
    s2 = sH * sH;
    tH = setLowWord(3.0 + s2 + r, 0);
    tL = r - (tH - 3.0 - s2);
    // u+v = ss*(1+...)
    u = sH * tH;
    v = sL * tH + tL * ss;
    // 2/(3log2)*(ss+...)
    pH = setLowWord(u + v, 0);
    pL = v - (pH - u);
    const zH = cpH * pH; // cpH+cpL = 2/(3*log2)
    const zL = cpL * pH + pL * cp + dpL[k];
    // log2(ax) = (ss+..)*2/(3*log2) = n + dpH + zH + zL
    t = n;
    t1 = setLowWord(zH + zL + dpH[k] + t, 0);
    t2 = zL - (t1 - t - dpH[k] - zH);
  }

  // split up y into y1+y2 and compute (y1+y2)*(t1+t2)
  const y1 = setLowWord(y, 0);
  pL = (y - y1) * t1 + y * t2;
  pH = y1 * t1;
  z = pL + pH;
  j = getHighWord(z);
  i = getLowWord(z) | 0;
  if (j >= 0x40900000) {
    // z >= 1024
    if (((j - 0x40900000) | i) !== 0) {
      return s * huge * huge; // overflow
    }
    if (pL + ovt > z - pH) {
      return s * huge * huge; // overflow
    }
  } else if ((j & 0x7fffffff) >= 0x4090cc00) {
    // z <= -1075
    if (((j - 0xc090cc00) | i) !== 0) {
      return s * tiny * tiny; // underflow
    }
    if (pL <= z - pH) {
      return s * tiny * tiny; // underflow
    }
  }

  // compute 2**(pH+pL)
  i = j & 0x7fffffff;
  k = (i >> 20) - 0x3ff;
  n = 0;
  if (i > 0x3fe00000) {
    // if |z| > 0.5, set n = [z+0.5]
    n = (j + (0x00100000 >> (k + 1))) | 0;
    k = ((n & 0x7fffffff) >> 20) - 0x3ff; // new k for n
    t = insertWords(n & ~(0x000fffff >> k), 0);
    n = ((n & 0x000fffff) | 0x00100000) >> (20 - k);
    if (j < 0) {
      n = -n;
    }
    pH -= t;
  }
  t = setLowWord(pL + pH, 0);
  u = t * lg2H;
  v = (pL - (t - pH)) * lg2 + t * lg2L;
  z = u + v;
  w = v - (z - u);
  t = z * z;
  t1 = z - t * (P1 + t * (P2 + t * (P3 + t * (P4 + t * P5))));
  r = (z * t1) / (t1 - 2 - (w + z * w));
  z = 1 - (r - z);
  j = (getHighWord(z) + (n << 20)) | 0;
  if (j >> 20 <= 0) {
    z = scalbn(z, n); // subnormal output
  } else {
    z = setHighWord(z, j);
  }
  return s * z;
}
